import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Users, Edit, Search, Star, Mail, Loader2, Trash2, FileText, DollarSign, RefreshCw, ShoppingCart, Calculator, Info, History, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// Import new customer modals
import { PurchaseHistoryModal } from "@/components/customers/PurchaseHistoryModal";
import { AccountStatementModal } from "@/components/customers/AccountStatementModal";
import { ReconcileModal } from "@/components/customers/ReconcileModal";
import { OpeningBalanceAuditModal } from "@/components/customers/OpeningBalanceAuditModal";
import TopCustomersTab from "@/components/customers/TopCustomersTab";
import BirthdayReminder from "@/components/BirthdayReminder";
import { AdditionalEmails } from "@/components/AdditionalEmails";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Customer level colors and badges
const CUSTOMER_LEVEL_CONFIG = {
  "Standard": { color: "bg-gray-100 text-gray-700", discount: 0 },
  "Level 1": { color: "bg-yellow-100 text-yellow-700", discount: 2 },
  "Level 2": { color: "bg-purple-100 text-purple-700", discount: 5 }
};

// Journal types for customers
const JOURNAL_TYPES = [
  { value: "PAYMENT", label: "Payment Received", description: "Record payment from customer" },
  { value: "CREDIT", label: "Credit Note", description: "Credit customer account" },
  { value: "DISCOUNT", label: "Discount", description: "Apply discount to account" },
  { value: "DEBIT", label: "Debit Adjustment", description: "Increase amount owed" },
  { value: "WRITE_OFF", label: "Write Off", description: "Write off bad debt" },
];

export const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [sendingStatement, setSendingStatement] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Opening Balance Audit modal
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  
  // History modal state
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyCustomer, setHistoryCustomer] = useState(null);
  const [historyTab, setHistoryTab] = useState("ledger");
  const [historyTransactions, setHistoryTransactions] = useState([]);
  const [historyFinancial, setHistoryFinancial] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Journal form state
  const [journalForm, setJournalForm] = useState({
    journal_type: "PAYMENT",
    amount: "",
    date: new Date().toISOString().split('T')[0],
    description: "",
    reference: ""
  });
  const [savingJournal, setSavingJournal] = useState(false);
  
  // New modal states
  const [showPurchaseHistory, setShowPurchaseHistory] = useState(false);
  const [purchaseHistoryCustomer, setPurchaseHistoryCustomer] = useState(null);
  const [showStatement, setShowStatement] = useState(false);
  const [statementCustomer, setStatementCustomer] = useState(null);
  const [showReconcile, setShowReconcile] = useState(false);
  const [reconcileCustomer, setReconcileCustomer] = useState(null);
  const [loyaltyMap, setLoyaltyMap] = useState({});

  useEffect(() => {
    fetchCustomers();
    fetchLoyaltySummary();
  }, []);

  // Derive the filtered list directly from state — no extra state, no
  // useEffect race. This means a background refetch can never briefly
  // clobber the user's search results.
  const filteredCustomers = useMemo(() => {
    if (!searchQuery) return customers;
    const query = searchQuery.toLowerCase();
    return customers.filter(
      (customer) =>
        customer.name?.toLowerCase().includes(query) ||
        (customer.company && customer.company.toLowerCase().includes(query)) ||
        (customer.contact_number && customer.contact_number.includes(query))
    );
  }, [searchQuery, customers]);

  const fetchCustomers = async (preserveScroll = false) => {
    // Store scroll position if we want to preserve it
    const scrollY = preserveScroll ? window.scrollY : 0;
    
    try {
      const response = await axios.get(`${API}/customers`, {
        params: {
          include_calculated_balance: true,  // Use calculated balance for accurate display
          limit: 5000,
        }
      });
      // Sort customers alphabetically by name
      const sortedCustomers = (response.data || []).sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      );
      setCustomers(sortedCustomers);
      
      // Restore scroll position after state update
      if (preserveScroll) {
        setTimeout(() => window.scrollTo(0, scrollY), 50);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  const fetchLoyaltySummary = async () => {
    try {
      const response = await axios.get(`${API}/loyalty/current-month-summary`);
      setLoyaltyMap(response.data?.customers || {});
    } catch (error) {
      console.error("Error fetching loyalty summary:", error);
    }
  };

  const handleSendStatement = async (customer) => {
    if (!customer.email) {
      toast.error("Customer does not have an email address");
      return;
    }
    
    setSendingStatement(customer.id);
    try {
      await axios.post(`${API}/email/statement`, {
        customer_id: customer.id,
        recipient_email: customer.email
      });
      toast.success(`Statement sent to ${customer.email}`);
    } catch (error) {
      console.error("Error sending statement:", error);
      toast.error(error.response?.data?.detail || "Failed to send statement");
    } finally {
      setSendingStatement(null);
    }
  };

  const handleDeleteClick = (customer) => {
    setCustomerToDelete(customer);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!customerToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/customers/${customerToDelete.id}`);
      toast.success(`Customer "${customerToDelete.name}" deleted`);
      setDeleteConfirmOpen(false);
      setCustomerToDelete(null);
      fetchCustomers(true);  // Preserve scroll position
    } catch (error) {
      console.error("Error deleting customer:", error);
      toast.error(error.response?.data?.detail || "Failed to delete customer");
    } finally {
      setDeleting(false);
    }
  };

  // Open customer history modal
  const openCustomerHistory = async (customer) => {
    setHistoryCustomer(customer);
    setHistoryTab("ledger");
    setShowHistoryModal(true);
    fetchCustomerHistory(customer.id);
  };

  // Fetch customer history data
  const fetchCustomerHistory = async (customerId) => {
    setLoadingHistory(true);
    try {
      const [financialRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/customers/${customerId}/financial`),
        axios.get(`${API}/customers/${customerId}/transactions`)
      ]);
      setHistoryFinancial(financialRes.data);
      setHistoryTransactions(transactionsRes.data.transactions || []);
    } catch (error) {
      console.error("Error fetching customer history:", error);
      toast.error("Failed to load customer history");
    } finally {
      setLoadingHistory(false);
    }
  };

  // Save journal entry
  const handleSaveJournal = async () => {
    if (!journalForm.amount || parseFloat(journalForm.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setSavingJournal(true);
    try {
      const res = await axios.post(`${API}/customers/${historyCustomer.id}/journal`, {
        journal_type: journalForm.journal_type,
        amount: parseFloat(journalForm.amount),
        date: journalForm.date,
        description: journalForm.description || `${journalForm.journal_type} entry`,
        reference: journalForm.reference,
        created_by: "Cashier 1",
        email_to_customer: !!journalForm.email_to_customer,
      });

      const emailRes = res?.data?.email_result;
      if (journalForm.email_to_customer) {
        if (emailRes?.status === "success") {
          toast.success("Journal entry saved — emailed to customer");
        } else if (emailRes?.status === "blocked") {
          toast.warning("Journal saved — email blocked (emails disabled in settings).");
        } else if (emailRes?.status === "error") {
          toast.warning(`Journal saved — email failed: ${emailRes.message || "see logs"}`);
        } else {
          toast.success("Journal entry saved successfully");
        }
      } else {
        toast.success("Journal entry saved successfully");
      }

      // Reset form
      setJournalForm({
        journal_type: "PAYMENT",
        amount: "",
        date: new Date().toISOString().split('T')[0],
        description: "",
        reference: "",
        email_to_customer: false,
      });
      
      // Refresh history data
      fetchCustomerHistory(historyCustomer.id);
      fetchCustomers(true); // Refresh customer list to update balances - preserve scroll
    } catch (error) {
      console.error("Error saving journal:", error);
      toast.error(error.response?.data?.detail || "Failed to save journal entry");
    } finally {
      setSavingJournal(false);
    }
  };

  const handleAddNew = () => {
    setEditingCustomer(null);
    setDialogOpen(true);
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCustomer(null);
  };

  const handleSave = async (customerData) => {
    try {
      if (editingCustomer) {
        await saveWithRetry(() => axios.put(`${API}/customers/${editingCustomer.id}`, customerData));
        toast.success("Customer updated successfully");
      } else {
        await saveWithRetry(() => axios.post(`${API}/customers`, customerData));
        toast.success("Customer created successfully");
      }
      handleCloseDialog();
      fetchCustomers(true);  // Preserve scroll position
    } catch (error) {
      console.error("Error saving customer:", error);
      toast.error(errorMessage(error, "Failed to save customer"));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading customers...</div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="customers-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Customers</h1>
          <p className="text-gray-600">Manage your customer accounts</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => window.open(`${API}/customers/credit-application-form.pdf`, '_blank')}
            data-testid="download-credit-application-btn"
            title="Print and hand to customers who want to open an account"
          >
            <FileText className="h-5 w-5 mr-2" />
            Account Application Form
          </Button>
          <Button
            onClick={() => setAuditModalOpen(true)}
            variant="outline"
            data-testid="audit-balances-btn"
          >
            <ShieldCheck className="h-5 w-5 mr-2" />
            Audit Opening Balances
          </Button>
          <Button
            onClick={handleAddNew}
            data-testid="add-customer-button"
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Customer
          </Button>
        </div>
      </div>

      <Tabs defaultValue="manage" className="w-full" data-testid="customers-tabs">
        <TabsList className="mb-4">
          <TabsTrigger value="manage" data-testid="customers-tab-manage">
            <Users className="h-4 w-4 mr-1" /> Manage Customers
          </TabsTrigger>
          <TabsTrigger value="top" data-testid="customers-tab-top">
            <Star className="h-4 w-4 mr-1" /> Top Customers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="manage">

      {/* Search and Refresh */}
      <div className="mb-6">
        <div className="flex gap-2 max-w-2xl">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by name, company, or contact number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="customer-search-input"
            />
          </div>
          <Button 
            variant="outline" 
            onClick={() => {
              setLoading(true);
              fetchCustomers();
            }}
            disabled={loading}
            data-testid="refresh-customers-btn"
          >
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        {searchQuery && (
          <p className="text-sm text-gray-600 mt-2">
            Found {filteredCustomers.length} customer(s)
          </p>
        )}
      </div>

      {/* Birthday Reminders Widget */}
      <BirthdayReminder />

      {filteredCustomers.length === 0 && !searchQuery ? (
        <Card>
          <CardContent className="text-center py-12">
            <Users className="h-16 w-16 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500 mb-4">No customers yet</p>
            <Button onClick={handleAddNew} data-testid="add-first-customer-button">
              <Plus className="h-5 w-5 mr-2" />
              Add Your First Customer
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredCustomers.map((customer) => {
            const levelConfig = CUSTOMER_LEVEL_CONFIG[customer.customer_level] || CUSTOMER_LEVEL_CONFIG["Standard"];
            return (
            <Card key={customer.id} data-testid={`customer-card-${customer.id}`} className={`hover:shadow-md transition-shadow ${customer.is_blocked ? 'border-red-300 bg-red-50' : ''}`}>
              <CardContent className="p-6">
                <div className="flex justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-semibold text-gray-900">{customer.name}</h3>
                      {customer.account_number && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                          {customer.account_number}
                        </span>
                      )}
                      {/* Customer Level Badge */}
                      <span className={`px-2 py-1 text-xs font-medium rounded flex items-center gap-1 ${levelConfig.color}`}>
                        {customer.customer_level !== "Standard" && <Star className="h-3 w-3" />}
                        {customer.customer_level || "Standard"}
                        {levelConfig.discount > 0 && ` (${levelConfig.discount}%)`}
                      </span>
                      {customer.has_vat_exemption && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                          VAT EXEMPT
                        </span>
                      )}
                      {customer.requires_order_number && (
                        <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded">
                          ORDER # REQ
                        </span>
                      )}
                      {customer.is_blocked && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded">
                          BLOCKED
                        </span>
                      )}
                    </div>
                    {customer.company && (
                      <p className="text-gray-600 mb-2">{customer.company}</p>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      {customer.contact_number && (
                        <div>
                          <span className="text-gray-600">Phone:</span>{" "}
                          <span className="font-medium">{customer.contact_number}</span>
                        </div>
                      )}
                      {customer.email && (
                        <div>
                          <span className="text-gray-600">Email:</span>{" "}
                          <span className="font-medium">{customer.email}</span>
                        </div>
                      )}
                      {customer.vat_number && (
                        <div>
                          <span className="text-gray-600">VAT:</span>{" "}
                          <span className="font-medium">{customer.vat_number}</span>
                        </div>
                      )}
                      {customer.discount_percent > 0 && (
                        <div>
                          <span className="text-gray-600">Discount:</span>{" "}
                          <span className="font-medium text-green-600">{customer.discount_percent}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-4">
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Balance Owing</div>
                      <div className={`text-2xl font-bold ${(customer.calculated_balance ?? customer.account_balance) > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                        R {(customer.calculated_balance ?? customer.account_balance)?.toFixed(2) || '0.00'}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Credit: R {customer.credit_limit?.toFixed(2) || '0.00'}
                      </div>
                      <div className="text-xs text-purple-600 font-medium mt-1" data-testid={`loyalty-earned-${customer.id}`}>
                        Loyalty this month: R {(loyaltyMap[customer.id]?.cashback_amount || 0).toFixed(2)}
                      </div>
                      {customer.loyalty_credit_balance > 0 && (
                        <div className="text-xs text-green-600 font-medium mt-0.5">
                          Loyalty balance: R {customer.loyalty_credit_balance?.toFixed(2)}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {customer.email && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSendStatement(customer)}
                          disabled={sendingStatement === customer.id}
                          className="border-blue-300 text-blue-600 hover:bg-blue-50"
                          data-testid={`email-statement-${customer.id}`}
                        >
                          {sendingStatement === customer.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Mail className="h-4 w-4 mr-1" />
                              Email
                            </>
                          )}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setPurchaseHistoryCustomer(customer);
                          setShowPurchaseHistory(true);
                        }}
                        data-testid={`purchases-customer-${customer.id}`}
                        className="border-purple-300 text-purple-600 hover:bg-purple-50"
                      >
                        <ShoppingCart className="h-4 w-4 mr-1" />
                        Purchases
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setStatementCustomer(customer);
                          setShowStatement(true);
                        }}
                        data-testid={`statement-customer-${customer.id}`}
                        className="border-blue-300 text-blue-600 hover:bg-blue-50"
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        Transactions
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setReconcileCustomer(customer);
                          setShowReconcile(true);
                        }}
                        data-testid={`reconcile-customer-${customer.id}`}
                        className="border-green-300 text-green-600 hover:bg-green-50"
                      >
                        <Calculator className="h-4 w-4 mr-1" />
                        Reconcile
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openCustomerHistory(customer)}
                        data-testid={`history-customer-${customer.id}`}
                      >
                        <Info className="h-4 w-4 mr-1" />
                        Account Info
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(customer)}
                        data-testid={`edit-customer-${customer.id}`}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteClick(customer)}
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        data-testid={`delete-customer-${customer.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
          })}
        </div>
      )}

      {filteredCustomers.length === 0 && searchQuery && (
        <Card>
          <CardContent className="text-center py-12">
            <Users className="h-16 w-16 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500">No customers found matching &ldquo;{searchQuery}&rdquo;</p>
          </CardContent>
        </Card>
      )}

        </TabsContent>

        <TabsContent value="top">
          <TopCustomersTab />
        </TabsContent>
      </Tabs>

      {/* Customer Dialog */}
      {dialogOpen && (
        <CustomerDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          customer={editingCustomer}
          onSave={handleSave}
        />
      )}
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setCustomerToDelete(null);
        }}
        customer={customerToDelete}
        onConfirm={handleDeleteConfirm}
        deleting={deleting}
      />

      {/* Customer History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Customer History: {historyCustomer?.name}
            </DialogTitle>
          </DialogHeader>

          {loadingHistory ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
            </div>
          ) : (
            <>
              {/* Financial Summary */}
              {historyFinancial && (
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-gray-500">Opening Balance</p>
                      <p className="text-xl font-bold">R {(historyFinancial.opening_balance || 0).toFixed(2)}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-gray-500">Total Sales</p>
                      <p className="text-xl font-bold text-blue-600">R {(historyFinancial.total_sales || 0).toFixed(2)}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm text-gray-500">Total Payments</p>
                      <p className="text-xl font-bold text-green-600">R {(historyFinancial.total_payments || 0).toFixed(2)}</p>
                    </CardContent>
                  </Card>
                  <Card className={historyFinancial.balance > 0 ? "border-red-300 bg-red-50" : "border-green-300 bg-green-50"}>
                    <CardContent className="pt-4">
                      <p className="text-sm text-gray-500">Balance Owing</p>
                      <p className={`text-xl font-bold ${historyFinancial.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        R {(historyFinancial.balance || 0).toFixed(2)}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}

              <Tabs value={historyTab} onValueChange={setHistoryTab}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="ledger">
                    <FileText className="h-4 w-4 mr-2" />
                    Transaction Ledger
                  </TabsTrigger>
                  <TabsTrigger value="journal">
                    <DollarSign className="h-4 w-4 mr-2" />
                    Journal Entry
                  </TabsTrigger>
                </TabsList>

                {/* Ledger Tab */}
                <TabsContent value="ledger" className="mt-4">
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead>Date</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Doc No</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Debit</TableHead>
                          <TableHead className="text-right">Credit</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {/* Note: Opening balance is included in transactions as DEBIT entries */}
                        {historyTransactions.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                              No transactions found
                            </TableCell>
                          </TableRow>
                        ) : (
                          historyTransactions.map((tx, idx) => (
                            <TableRow key={tx.id || idx}>
                              <TableCell>{tx.date}</TableCell>
                              <TableCell>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  tx.type === 'INVOICE' ? 'bg-blue-100 text-blue-700' :
                                  tx.type.includes('Payment') ? 'bg-green-100 text-green-700' :
                                  tx.type === 'CREDIT' || tx.type === 'DISCOUNT' ? 'bg-purple-100 text-purple-700' :
                                  tx.type === 'WRITE_OFF' ? 'bg-red-100 text-red-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {tx.type}
                                </span>
                              </TableCell>
                              <TableCell>{tx.doc_no || '-'}</TableCell>
                              <TableCell>{tx.description}</TableCell>
                              <TableCell className="text-right text-red-600 font-medium">
                                {tx.debit > 0 ? `R ${tx.debit.toFixed(2)}` : '-'}
                              </TableCell>
                              <TableCell className="text-right text-green-600 font-medium">
                                {tx.credit > 0 ? `R ${tx.credit.toFixed(2)}` : '-'}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>

                {/* Journal Entry Tab */}
                <TabsContent value="journal" className="mt-4">
                  <Card>
                    <CardContent className="pt-6 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Journal Type</Label>
                          <Select
                            value={journalForm.journal_type}
                            onValueChange={(value) => setJournalForm(prev => ({ ...prev, journal_type: value }))}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              {[...JOURNAL_TYPES].sort((a, b) => (a.label || '').localeCompare(b.label || '')).map(type => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-gray-500 mt-1">
                            {JOURNAL_TYPES.find(t => t.value === journalForm.journal_type)?.description}
                          </p>
                        </div>
                        <div>
                          <Label>Date</Label>
                          <Input
                            type="date"
                            value={journalForm.date}
                            onChange={(e) => setJournalForm(prev => ({ ...prev, date: e.target.value }))}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Amount (R)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            value={journalForm.amount}
                            onChange={(e) => setJournalForm(prev => ({ ...prev, amount: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label>Reference</Label>
                          <Input
                            placeholder="Receipt/Reference number"
                            value={journalForm.reference}
                            onChange={(e) => setJournalForm(prev => ({ ...prev, reference: e.target.value }))}
                          />
                        </div>
                      </div>

                      <div>
                        <Label>Description</Label>
                        <Input
                          placeholder="Enter description..."
                          value={journalForm.description}
                          onChange={(e) => setJournalForm(prev => ({ ...prev, description: e.target.value }))}
                        />
                      </div>

                      {/* Email transaction confirmation to customer — opt-in, never automatic */}
                      <label className="flex items-start gap-2 cursor-pointer border rounded p-3 bg-blue-50 border-blue-200">
                        <input
                          type="checkbox"
                          checked={!!journalForm.email_to_customer}
                          disabled={!historyCustomer?.email}
                          onChange={(e) => setJournalForm(prev => ({ ...prev, email_to_customer: e.target.checked }))}
                          className="mt-1 rounded"
                          data-testid="journal-history-email-to-customer-checkbox"
                        />
                        <span className="text-sm">
                          <span className="block font-medium text-blue-700">
                            Email this transaction to the customer
                          </span>
                          {historyCustomer?.email ? (
                            <span className="block text-xs text-gray-600 mt-1">
                              Will be sent to <span className="font-mono">{historyCustomer.email}</span>
                              {" "}plus any additional emails configured for receipts.
                            </span>
                          ) : (
                            <span className="block text-xs text-amber-700 mt-1">
                              Customer has no email address on file — add one before enabling.
                            </span>
                          )}
                        </span>
                      </label>

                      <Button 
                        onClick={handleSaveJournal} 
                        disabled={savingJournal}
                        className="w-full bg-purple-600 hover:bg-purple-700"
                      >
                        {savingJournal ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <DollarSign className="h-4 w-4 mr-2" />
                            Save Journal Entry
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Purchase History Modal */}
      <PurchaseHistoryModal
        open={showPurchaseHistory}
        onClose={() => {
          setShowPurchaseHistory(false);
          setPurchaseHistoryCustomer(null);
        }}
        customer={purchaseHistoryCustomer}
      />

      {/* Account Statement Modal */}
      <AccountStatementModal
        open={showStatement}
        onClose={() => {
          setShowStatement(false);
          setStatementCustomer(null);
        }}
        customer={statementCustomer}
      />

      {/* Reconcile Modal */}
      <ReconcileModal
        open={showReconcile}
        onClose={() => {
          setShowReconcile(false);
          setReconcileCustomer(null);
        }}
        customer={reconcileCustomer}
        onReconciled={() => fetchCustomers(true)}  // Preserve scroll position
      />

      {/* Opening Balance Audit Modal */}
      <OpeningBalanceAuditModal
        open={auditModalOpen}
        onOpenChange={setAuditModalOpen}
        onSelectCustomer={(suspect) => {
          const c = customers.find((x) => x.id === suspect.customer_id);
          if (c) {
            setAuditModalOpen(false);
            handleEdit(c);
          } else {
            toast.error("Customer not loaded — refresh the list and try again");
          }
        }}
      />
    </div>
  );
};

// Customer Dialog Component
const CustomerDialog = ({ open, onClose, customer, onSave }) => {
  const getInitialFormData = () => customer ? {
    ...customer,
    account_balance: customer.account_balance || 0,
  } : {
    name: "",
    company: "",
    contact_number: "",
    email: "",
    vat_number: "",
    id_number: "",
    street_address: "",
    suburb: "",
    province: "",
    credit_limit: "",
    discount_percent: "",
    customer_level: "Standard",
    has_vat_exemption: false,
    vat_exemption_number: "",
    is_blocked: false,
    account_balance: 0,
  };

  const [formData, setFormData] = useState(getInitialFormData);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!formData.name) {
      toast.error("Customer name is required");
      return;
    }

    const submitData = {
      ...formData,
      credit_limit: parseFloat(formData.credit_limit) || 0,
      discount_percent: parseFloat(formData.discount_percent) || 0,
      opening_balance: parseFloat(formData.account_balance) || 0,
    };

    onSave(submitData);
  };

  const levelConfig = CUSTOMER_LEVEL_CONFIG[formData.customer_level] || CUSTOMER_LEVEL_CONFIG["Standard"];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {customer ? `Edit Customer - ${customer.name}` : "Add New Customer"}
          </DialogTitle>
          <div className="flex items-center gap-2">
            {customer?.account_number && (
              <span className="text-sm text-blue-600 font-medium">Account: {customer.account_number}</span>
            )}
            {formData.customer_level && formData.customer_level !== "Standard" && (
              <span className={`px-2 py-0.5 text-xs font-medium rounded ${levelConfig.color}`}>
                {formData.customer_level} ({levelConfig.discount}% auto discount)
              </span>
            )}
          </div>
        </DialogHeader>

        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="account">Account Settings</TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  data-testid="customer-name-input"
                />
              </div>
              <div>
                <Label>Company</Label>
                <Input
                  value={formData.company || ""}
                  onChange={(e) => handleChange("company", e.target.value)}
                  data-testid="customer-company-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Number</Label>
                <Input
                  value={formData.contact_number || ""}
                  onChange={(e) => handleChange("contact_number", e.target.value)}
                  data-testid="customer-contact-input"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email || ""}
                  onChange={(e) => handleChange("email", e.target.value)}
                  data-testid="customer-email-input"
                />
                {formData.email && (
                  <label className="flex items-center gap-2 mt-2 text-xs text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!formData.email_receipts_opt_out}
                      onChange={(e) => handleChange("email_receipts_opt_out", e.target.checked)}
                      className="rounded"
                      data-testid="customer-email-optout-checkbox"
                    />
                    Do not auto-email invoices to this customer
                  </label>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <Label>Farming VAT Certificate #</Label>
                <Input
                  value={formData.farming_vat_cert_number || ""}
                  onChange={(e) => handleChange("farming_vat_cert_number", e.target.value)}
                  placeholder="e.g. 4123456789"
                  data-testid="customer-vat-cert-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Required to justify zero-rated feed / lucerne sales to this customer.
                </p>
              </div>
              <div>
                <Label>Certificate expiry</Label>
                <Input
                  type="date"
                  value={formData.farming_vat_cert_expiry || ""}
                  onChange={(e) => handleChange("farming_vat_cert_expiry", e.target.value)}
                  data-testid="customer-vat-cert-expiry-input"
                />
              </div>
            </div>

            {/* Additional Emails Section - only show for existing customers */}
            {formData.id && (
              <AdditionalEmails 
                customerId={formData.id} 
                customerName={formData.name}
                primaryEmail={formData.email}
              />
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>VAT Number</Label>
                <Input
                  value={formData.vat_number || ""}
                  onChange={(e) => handleChange("vat_number", e.target.value)}
                  data-testid="customer-vat-input"
                />
              </div>
              <div>
                <Label>ID Number</Label>
                <Input
                  value={formData.id_number || ""}
                  onChange={(e) => handleChange("id_number", e.target.value)}
                  data-testid="customer-id-input"
                />
              </div>
            </div>

            <div>
              <Label>Street Address</Label>
              <Input
                value={formData.street_address || ""}
                onChange={(e) => handleChange("street_address", e.target.value)}
                data-testid="customer-address-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Suburb</Label>
                <Input
                  value={formData.suburb || ""}
                  onChange={(e) => handleChange("suburb", e.target.value)}
                />
              </div>
              <div>
                <Label>Province</Label>
                <Input
                  value={formData.province || ""}
                  onChange={(e) => handleChange("province", e.target.value)}
                />
              </div>
            </div>
          </TabsContent>

          {/* Account Settings Tab */}
          <TabsContent value="account" className="space-y-4">
            {/* Customer Level Section */}
            <div className="border rounded-lg p-4 bg-gradient-to-r from-yellow-50 to-purple-50">
              <Label className="text-lg mb-3 block">Customer Level</Label>
              <Select
                value={formData.customer_level || "Standard"}
                onValueChange={(value) => handleChange("customer_level", value)}
              >
                <SelectTrigger className="w-full" data-testid="customer-level-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Standard">
                    <div className="flex items-center gap-2">
                      <span>Standard</span>
                      <span className="text-xs text-gray-500">- No automatic discount</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="Level 1">
                    <div className="flex items-center gap-2">
                      <Star className="h-3 w-3 text-yellow-500" />
                      <span>Level 1</span>
                      <span className="text-xs text-yellow-600 font-medium">- 2% automatic discount</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="Level 2">
                    <div className="flex items-center gap-2">
                      <Star className="h-3 w-3 text-purple-500" />
                      <span>Level 2</span>
                      <span className="text-xs text-purple-600 font-medium">- 5% automatic discount</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-2">
                Customer level discounts are applied automatically to eligible products at checkout.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Credit Limit (R)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.credit_limit || ""}
                  onChange={(e) => handleChange("credit_limit", e.target.value)}
                  placeholder="0.00"
                  data-testid="customer-credit-limit-input"
                />
              </div>
              <div>
                <Label>Additional Discount %</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.discount_percent || ""}
                  onChange={(e) => handleChange("discount_percent", e.target.value)}
                  placeholder="0.00"
                  data-testid="customer-discount-input"
                />
                <p className="text-xs text-gray-500 mt-1">Extra discount on top of level discount</p>
              </div>
            </div>

            {customer && (
              <div className="bg-gray-100 p-4 rounded">
                <Label className="text-lg">Opening Balance</Label>
                <p className="text-xs text-gray-500 mb-2">Adjust the imported opening balance if incorrect</p>
                <div className="flex items-center gap-2">
                  <span className="text-xl font-bold">R</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.account_balance || 0}
                    onChange={(e) => handleChange("account_balance", parseFloat(e.target.value) || 0)}
                    className="text-xl font-bold max-w-[200px]"
                    data-testid="customer-balance-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Positive = Customer owes you | Negative = You owe customer
                </p>
              </div>
            )}

            {/* Block Account Section */}
            <div className="border-t pt-4">
              <Label className="text-lg mb-3 block text-red-700">Account Status</Label>
              <div className={`flex items-center space-x-2 p-3 rounded ${formData.is_blocked ? 'bg-red-100 border border-red-300' : 'bg-gray-50'}`}>
                <Checkbox
                  id="is_blocked"
                  checked={formData.is_blocked || false}
                  onCheckedChange={(checked) => handleChange("is_blocked", checked)}
                  data-testid="customer-blocked-checkbox"
                />
                <Label htmlFor="is_blocked" className={`font-medium ${formData.is_blocked ? 'text-red-700' : 'text-gray-700'}`}>
                  Block Account - No sales allowed
                </Label>
              </div>
              {formData.is_blocked && (
                <p className="text-xs text-red-600 mt-2">
                  ⚠️ This customer cannot make account purchases while blocked
                </p>
              )}
            </div>

            <div className="border-t pt-4">
              <Label className="text-lg mb-3 block">VAT Exemption</Label>
              <div className="flex items-center space-x-2 mb-3">
                <Checkbox
                  id="has_vat_exemption"
                  checked={formData.has_vat_exemption || false}
                  onCheckedChange={(checked) => handleChange("has_vat_exemption", checked)}
                />
                <Label htmlFor="has_vat_exemption" className="font-normal">
                  Customer has VAT Exemption Certificate
                </Label>
              </div>

              {formData.has_vat_exemption && (
                <div>
                  <Label>VAT Exemption Certificate Number</Label>
                  <Input
                    value={formData.vat_exemption_number || ""}
                    onChange={(e) => handleChange("vat_exemption_number", e.target.value)}
                    placeholder="Enter certificate number"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Products marked as &ldquo;VAT Exempt Eligible&rdquo; will have 0% VAT for this customer
                  </p>
                </div>
              )}
            </div>

            {/* Requires Order Number */}
            <div className="border-t pt-4">
              <Label className="text-lg mb-3 block">Order Requirements</Label>
              <div className={`border rounded-lg p-4 ${formData.requires_order_number ? 'bg-blue-50 border-blue-300' : 'bg-gray-50'}`}>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="requires_order_number"
                    checked={formData.requires_order_number || false}
                    onCheckedChange={(checked) => handleChange("requires_order_number", checked)}
                    data-testid="requires-order-number-checkbox"
                  />
                  <Label htmlFor="requires_order_number" className={`font-medium ${formData.requires_order_number ? 'text-blue-700' : 'text-gray-700'}`}>
                    Requires Order Number / Reference
                  </Label>
                </div>
                <p className="text-xs text-gray-500 mt-2 ml-6">
                  When enabled, sales to this customer will require an order number or reference to be entered before checkout.
                </p>
                {formData.requires_order_number && (
                  <p className="text-xs text-blue-600 mt-2 ml-6">
                    ✓ Order number will be mandatory at checkout and printed on invoices
                  </p>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} className="bg-purple-600 hover:bg-purple-700">
            {customer ? "Save Changes" : "Create Customer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Delete Confirmation Dialog Component
const DeleteConfirmDialog = ({ open, onClose, customer, onConfirm, deleting }) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-red-600 flex items-center gap-2">
            <Trash2 className="h-5 w-5" />
            Delete Customer
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          <p className="text-gray-700">
            Are you sure you want to delete <span className="font-semibold">{customer?.name}</span>?
          </p>
          {customer?.account_balance !== 0 && (
            <p className="text-amber-600 text-sm mt-2">
              ⚠️ This customer has an account balance of R {customer?.account_balance?.toFixed(2)}
            </p>
          )}
          <p className="text-gray-500 text-sm mt-2">
            This action cannot be undone.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            onClick={onConfirm} 
            disabled={deleting}
            className="bg-red-600 hover:bg-red-700"
          >
            {deleting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Customers;
